import React, { useState } from 'react';

import PropTypes from 'prop-types';

import { ConditionalViewProvider, useConditionalView } from '@ulta/core/providers/ConditionalViewProvider/ConditionalViewProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import AsyncComponent from '../AsyncComponent/AsyncComponent';
import * as utils from './ConditionalViewRenderer';

/**
 * Represents a ConditionalViewRenderer component
 *
 * @method
 * @param { ConditionalViewRendererModulesProps} props - React properties passed from composition
 * @returns ConditionalViewRenderer
 */
export const ConditionalViewRendererModules = ( props ) => {
  const { moduleRefs, modules } = props;

  if( !moduleRefs || !modules ){
    return null;
  }

  return utils.mapRefsToModules( { moduleRefs, modules } )?.map( ( module, index ) => (
    <React.Fragment key={ module.id }>
      <AsyncComponent
        moduleName={ module.moduleName }
        { ...spreadProps( { ...module } ) }
      />
    </React.Fragment>
  ) );
};

/**
 * Property type definitions
 * @typedef ConditionalViewRendererModulesProps
 * @type {object}
 */
export const ConditionalViewRendererModulesProps = {
  moduleRefs: PropTypes.arrayOf( PropTypes.string ),
  modules: PropTypes.arrayOf( PropTypes.object )
};
ConditionalViewRendererModules.propTypes = ConditionalViewRendererModulesProps;

/**
 * Represents a ConditionalViewRenderer component
 *
 * @method
 * @param { ConditionalViewRendererProps} props - React properties passed from composition
 * @returns ConditionalViewRenderer
 */
export const ConditionalViewRenderer = function( props ){
  const { viewType } = useConditionalView();
  const [conditionalViewType, setConditonalViewType] = useState( viewType );
  const { conditionalViews, defaultModules, modules } = props;

  if( !defaultModules && !conditionalViews ){
    return null;
  }

  let moduleRefs = conditionalViews.find( conditionalView => conditionalView.key === conditionalViewType )?.moduleRefs;
  if( conditionalViewType === 'default' ){
    moduleRefs = defaultModules?.moduleRefs;
  }

  return (
    <ConditionalViewProvider setConditonalViewType={ setConditonalViewType }>
      <div className='ConditionalViewRenderer'>
        <ConditionalViewRendererModules moduleRefs={ moduleRefs }
          modules={ modules }
        />
      </div>
    </ConditionalViewProvider>
  );
};

/**
 * Property type definitions
 * @typedef ConditionalViewRendererProps
 * @type {object}
 */
export const ConditionalViewRendererProps = {
  conditionalViews: PropTypes.arrayOf( PropTypes.object ),
  defaultModules: PropTypes.object,
  modules: PropTypes.arrayOf( PropTypes.object )
};
ConditionalViewRenderer.propTypes = ConditionalViewRendererProps;

/**
 * mapRefsToModules function for mapping module ids to modules
 * @method
 * @param {Object} data - Passing moduleRefs and modules as a data argument
 * @returns {array} - Returns reduced array of moduleRefs, replaced by actual module data.
 */
export const mapRefsToModules = ( data ) => {
  const { moduleRefs, modules } = data;
  return moduleRefs.reduce( ( acc, curr ) => {
    const foundModule = modules.find( el => el.id === curr );
    if( foundModule ){
      return [...acc, foundModule];
    }
    return acc;
  }, [] );

};